import { Radar, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";
import { SubscriptionPricing } from "../components/SubscriptionPricing";

const CATEGORIES = ["General", "SaaS", "E-commerce", "Finance", "Healthcare", "Consumer Goods", "Agency / Services"];

export const Landing = () => {
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("General");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const startAudit = async (e) => {
    e.preventDefault();
    if (!brand.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/audits/start`, { brand_name: brand.trim(), category });
      navigate(`/audit/${data.audit_id}`);
    } catch (err) {
      toast.error("Could not start audit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-obsidian">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,42,59,0.14) 0%, rgba(6,8,14,0) 60%)" }}
      />
      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-6 pt-28 pb-24 text-center" id="free-audit">
        <div className="mb-6 flex items-center gap-2 rounded-full border border-surface-border bg-surface-card px-4 py-1.5 fade-up" data-testid="hero-badge">
          <Radar className="h-4 w-4 text-sovereign" />
          <span className="text-xs font-medium uppercase tracking-widest text-slate-400">Multi-AI Reputation Intelligence</span>
        </div>
        <h1 className="mb-6 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl fade-up" style={{ animationDelay: "80ms" }} data-testid="hero-title">
          Is AI telling the truth about <span className="text-gradient-sovereign">your brand?</span>
        </h1>
        <p className="mb-10 max-w-xl text-base text-slate-400 fade-up" style={{ animationDelay: "160ms" }} data-testid="hero-subtitle">
          BrandSignal probes OpenAI, Claude, Gemini, and Perplexity in real time — flags hallucinations in Sovereign Red, and hands you the fix.
        </p>
        <form onSubmit={startAudit} className="w-full max-w-lg fade-up" style={{ animationDelay: "240ms" }}>
          <div className="flex flex-col gap-3 rounded-lg border border-surface-border bg-surface-card p-3 sm:flex-row">
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="Enter your Brand"
              className="flex-1 rounded-md bg-transparent px-3 py-3 text-base text-white outline-none placeholder:text-slate-500"
              data-testid="brand-input"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-md border border-surface-border bg-obsidian px-3 py-3 text-sm text-slate-300 outline-none"
              data-testid="category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <Button type="submit" disabled={loading} className="gap-2 bg-sovereign px-6 text-white hover:bg-sovereign/90" data-testid="run-audit-btn">
              {loading ? "Scanning..." : "Run Free Audit"} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </form>
        <p className="mt-4 text-xs text-slate-500 fade-up" style={{ animationDelay: "300ms" }}>No credit card. Free Pulse Check score + Hallucination Warnings included.</p>

        <div className="mt-20 grid w-full grid-cols-2 gap-4 sm:grid-cols-4 fade-up" style={{ animationDelay: "380ms" }} data-testid="provider-grid">
          {[
            { name: "OpenAI", color: "#00F2FE" },
            { name: "Claude", color: "#A855F7" },
            { name: "Gemini", color: "#EC4899" },
            { name: "Perplexity", color: "#14B8A6" },
          ].map((p) => (
            <div key={p.name} className="rounded-lg border border-surface-border bg-surface-card px-4 py-5">
              <div className="mx-auto mb-2 h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
              <p className="text-sm font-medium text-slate-300">{p.name}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <SubscriptionPricing />
      </div>
    </div>
  );
};
